import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import { API_BASE_URL } from '../api-base-url.token';

/** Code brut (deviceTypeCode) → clé i18n `equipements.upsertModal.deviceTypes.<key>` — source unique
 *  partagée entre la modale d'ajout/édition et la modale de détail. */
export const DEVICE_TYPE_KEYS: Record<string, string> = {
  PEOPLE_COUNTER: 'peopleCounter',
  TEMP_HUMIDITY: 'tempHumidity',
  CO2: 'co2',
};

export interface SensorUpsertRequest {
  name: string;
  deviceEui: string;
  deviceType: string;
  location: string | null;
  queueId: string | null;
  active: boolean;
}

export interface QueueOption {
  id: string;
  name: string;
}

export interface FrameSummary {
  receivedAt: string;
  rssi: number | null;
  snr: number | null;
  spreadingFactor: number | null;
  periodicIn: number | null;
  periodicOut: number | null;
  accumulatedIn: number | null;
  accumulatedOut: number | null;
}

export interface LatestFrameDetail {
  receivedAt: string;
  fetchedAt: string;
  orangeMessageId: string | null;
  payloadHex: string | null;
  payloadDecoded: Record<string, unknown> | null;
  frameCounter: number | null;
  rssi: number | null;
  snr: number | null;
  spreadingFactor: number | null;
  gatewayId: string | null;
}

export interface SensorDetail {
  id: string;
  name: string;
  deviceEui: string;
  deviceType: string | null;
  deviceTypeCode: string | null;
  location: string | null;
  active: boolean;
  queueId: string | null;
  createdAt: string;
  updatedAt: string;
  lastFrameAt: string | null;
  lastFetchCursor: string | null;
  latestFrame: LatestFrameDetail | null;
  recentFrames: FrameSummary[];
}

export interface Equipement {
  id: string;
  capteur: string;
  deviceEui: string;
  deviceType: string | null;
  deviceTypeCode: string | null;
  lieu: string | null;
  queueId: string | null;
  statut: 'OK' | 'Offline' | 'Instable';
  derniereDonnee: string;
  derniereTrameAt: string | null;
  batterie: number | null;
  nodeBatterie: number | null;
  rssi: number | null;
  snr: number | null;
  spreadingFactor: number | null;
  periodicIn: number | null;
  periodicOut: number | null;
  accumulatedIn: number | null;
  accumulatedOut: number | null;
  establishmentId: string | null;
  establishmentName: string | null;
  active: boolean;
  simulated?: boolean;
}

export interface EquipementGlobal {
  total: number;
  enLigne: number;
  offline: number;
  instable: number;
  batterieFaible: number;
}

export interface EquipementSnapshot {
  global: EquipementGlobal;
  equipements: Equipement[];
}

@Injectable({ providedIn: 'root' })
export class EquipementService {
  private readonly http = inject(HttpClient);
  private readonly baseRoot = inject(API_BASE_URL).replace(/\/+$/, '');
  private readonly base = `${this.baseRoot}/api/v1/equipements`;

  /** Snapshot semi-live (capteurs) : mutualisé un court instant (TTL) pour dédupliquer les montées
   *  simultanées de plusieurs composants (site/pilotage/equipements), sans servir de données périmées
   *  au polling 5 min de la page pilotage. */
  private orgSnapshot$?: Observable<EquipementSnapshot>;
  private static readonly ORG_SNAPSHOT_TTL_MS = 30_000;

  getOrgSnapshot(): Observable<EquipementSnapshot> {
    if (!this.orgSnapshot$) {
      this.orgSnapshot$ = this.http.get<EquipementSnapshot>(`${this.base}/org/snapshot`).pipe(shareReplay(1));
      setTimeout(() => { this.orgSnapshot$ = undefined; }, EquipementService.ORG_SNAPSHOT_TTL_MS);
    }
    return this.orgSnapshot$;
  }

  getSnapshot(establishmentId: string): Observable<EquipementSnapshot> {
    return this.http.get<EquipementSnapshot>(
      `${this.base}/establishments/${encodeURIComponent(establishmentId)}/snapshot`
    );
  }

  getSensorDetail(establishmentId: string, sensorId: string): Observable<SensorDetail> {
    return this.http.get<SensorDetail>(
      `${this.base}/establishments/${encodeURIComponent(establishmentId)}/sensors/${encodeURIComponent(sensorId)}`
    );
  }

  // DEPRECATED (2026-09-08) : createSensor/updateSensor/deleteSensor uniquement -- pendant front
  // des 3 endpoints d'écriture marqués côté EquipementsController (waitora-backend). Seul appelant :
  // equipements.component.ts (page /equipements, orpheline depuis le retrait de l'entrée sidebar).
  // getOrgSnapshot/getSnapshot/getSensorDetail restent utilisés par supervision.component.ts,
  // ne pas y toucher.
  createSensor(establishmentId: string, req: SensorUpsertRequest): Observable<{ id: string }> {
    return this.http.post<{ id: string }>(
      `${this.base}/establishments/${encodeURIComponent(establishmentId)}/sensors`,
      req
    );
  }

  updateSensor(establishmentId: string, sensorId: string, req: SensorUpsertRequest): Observable<void> {
    return this.http.put<void>(
      `${this.base}/establishments/${encodeURIComponent(establishmentId)}/sensors/${encodeURIComponent(sensorId)}`,
      req
    );
  }

  deleteSensor(establishmentId: string, sensorId: string): Observable<void> {
    return this.http.delete<void>(
      `${this.base}/establishments/${encodeURIComponent(establishmentId)}/sensors/${encodeURIComponent(sensorId)}`
    );
  }
}
